import { useBizyProfile } from '../../../queries/bizy-profile';

/**
 * The subset of user fields the greeting fallback reads. Both the who-am-i
 * `User` and Bizy Desktop's `DesktopUser` shapes satisfy this.
 */
interface GreetingUserFields {
	preferred_first_name?: string | null;
	first_name?: string | null;
	display_name?: string | null;
}

/**
 * The surface's own first-name fallback, before the Bizy nickname is layered
 * on: the user's preferred first name, else first name, else the first token of
 * the display name. Defined once so all greeting surfaces (web side panel, the
 * People Browser greeter callout, and Bizy Desktop) stay in lockstep.
 */
export function bizyGreetingFallbackName(user: GreetingUserFields | null | undefined): string {
	return user?.preferred_first_name || user?.first_name || user?.display_name?.split(' ')[0] || '';
}

/**
 * The name Bizy greets the user by: the Bizy personalization nickname ("call
 * me JT") when set, matching how Bizy itself addresses the user (the system
 * prompt prefers bizy_nickname), falling back to the surface's own notion of
 * the user's first name. Shared by the web side panel and Bizy Desktop so
 * both greet identically. The settings modal shares the same query cache, so
 * a nickname edit there updates the greeting live.
 */
export function useBizyGreetingDisplayName(fallbackName: string, options?: { enabled?: boolean }): string;
export function useBizyGreetingDisplayName(fallbackName: string | undefined, options?: { enabled?: boolean }): string | undefined;
export function useBizyGreetingDisplayName(fallbackName: string | undefined, options: { enabled?: boolean } = {}): string | undefined {
	const { data: bizyPersonalProfile } = useBizyProfile({ enabled: options.enabled ?? true });
	const bizyNickname = bizyPersonalProfile?.fields.bizy_nickname?.trim();
	return bizyNickname || fallbackName;
}
